import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';
import type { TeamMember, TeamMemberFormData } from '../types';

const QK = ['team-members'];
const ADMIN_QK = ['team-members-admin'];

function invalidateLists(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: QK });
  qc.invalidateQueries({ queryKey: ADMIN_QK });
}

function refreshPhoto(id: string, qc: ReturnType<typeof useQueryClient>): void {
  pb.send(`/api/team-members/refresh-photo/${id}`, { method: 'POST' })
    .then(() => qc.invalidateQueries({ queryKey: QK }))
    .catch(() => {});
}

export function useCreateTeamMember() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: TeamMemberFormData) =>
      pb.send<TeamMember>('/api/team-members/create', {
        method: 'POST',
        body: data,
      }),
    onSuccess: (res: TeamMember) => {
      qc.invalidateQueries({ queryKey: QK });
      toast('Miembro creado', 'success');
      if (res.id) refreshPhoto(res.id, qc);
    },
    onError: () => toast('Error al crear miembro', 'error'),
  });
}

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (v: { id: string; data: Partial<TeamMember> }) =>
      pb.collection('team_members').update(v.id, v.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      toast('Miembro actualizado', 'success');
    },
    onError: () => toast('Error al actualizar', 'error'),
  });
}

export function useArchiveTeamMember() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) =>
      pb.send(`/api/team-members/archive/${id}`, { method: 'POST' }),
    onSuccess: () => {
      invalidateLists(qc);
      toast('Miembro archivado', 'success');
    },
    onError: () => toast('Error al archivar', 'error'),
  });
}

export function useRestoreTeamMember() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) =>
      pb.send(`/api/team-members/restore/${id}`, { method: 'POST' }),
    onSuccess: () => {
      invalidateLists(qc);
      toast('Miembro restaurado', 'success');
    },
    onError: () => toast('Error al restaurar', 'error'),
  });
}

export function useIssueTempPassword() {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) =>
      pb.send<{ temp_password: string }>(
        `/api/team-members/issue-temp-password/${id}`,
        { method: 'POST' }
      ),
    onError: () => toast('Error al reiniciar contraseña', 'error'),
  });
}

export function useToggleMemberActive() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (v: { id: string; is_active: boolean }) =>
      pb.collection('team_members').update(v.id, { is_active: !v.is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      toast('Estado actualizado', 'success');
    },
    onError: () => toast('Error al cambiar estado', 'error'),
  });
}
